import {
  HSE_ACTION_KINDS,
  HSE_PRIORITIES,
  HSE_REPORT_TYPES,
  type HseReportType,
  type ReauthKind,
} from '@felix/domain';
import { Button, Group, Modal, Select, Switch, Textarea, TextInput } from '@mantine/core';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { DayInput } from '../../inputs/DayInput.js';
import classes from './Hse.module.css';
import { hseErrorText } from './parts.js';
import type {
  HseAction,
  HseActionInput,
  HseDetail,
  HseFinding,
  HseFindingInput,
  HseOptions,
  HseWorkOrderInput,
} from './types.js';
import { workOrderInput } from './work-order.js';

/** Pencerenin gönderimi: hata metni ya da `null` (başarılı). */
type Submit = () => Promise<unknown>;

function useSubmit(onClose: () => void, onError?: (failure: unknown) => void) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (work: Submit) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onClose();
    } catch (failure) {
      onError?.(failure);
      setError(hseErrorText(t, failure));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run, reset: () => setError(null) };
}

function Shell({
  opened,
  title,
  onClose,
  error,
  busy,
  confirmLabel,
  disabled,
  color,
  onSubmit,
  children,
}: {
  opened: boolean;
  title: string;
  onClose: () => void;
  error: string | null;
  busy: boolean;
  confirmLabel: string;
  disabled?: boolean;
  color?: string;
  onSubmit: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="lg"
      closeButtonProps={{ 'aria-label': t('hse.dialog.close') }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!disabled) onSubmit();
        }}
      >
        <div className={classes.cardBody}>{children}</div>
        {error ? (
          <p className={classes.banner} data-tone="danger" role="alert">
            {error}
          </p>
        ) : null}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {t('hse.dialog.cancel')}
          </Button>
          <Button
            type="submit"
            loading={busy}
            disabled={disabled ?? false}
            {...(color ? { color } : {})}
          >
            {confirmLabel}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}

/**
 * İmza ve gerekçe penceresi: kapanışa gönderim, karar, yeniden açma, iptal, vazgeçme. Parola
 * HSE türlerinde isteğe bağlıdır (`reauthKind`; ADR 0056): alan yerine bilgi metni görünür; şirket
 * ayarı açıksa ya da sunucu isterse alan görünür.
 */
export function ConfirmDialog(props: {
  opened: boolean;
  title: string;
  body: string;
  reason: 'required' | 'optional' | 'none';
  reasonLabel?: string;
  password: boolean;
  /** Onayın kayıt türü (ADR 0056 §1); verilmezse parola zorunlu. */
  reauthKind?: ReauthKind;
  confirmLabel: string;
  color?: string;
  onClose: () => void;
  /** Parola yalnız girildiyse dolu gelir; çağıran boşsa göndermez. */
  onConfirm: (input: { reason: string; password: string }) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const reauth = useReauth(props.reauthKind);
  const close = () => {
    setReason('');
    reauth.reset();
    submit.reset();
    props.onClose();
  };
  const submit = useSubmit(close, reauth.noteError);
  const password = props.password ? (reauth.body().password ?? '') : '';
  const missing =
    (props.reason === 'required' && reason.trim() === '') || (props.password && reauth.missing);
  return (
    <Shell
      opened={props.opened}
      title={props.title}
      onClose={close}
      error={submit.error}
      busy={submit.busy}
      confirmLabel={props.confirmLabel}
      disabled={missing}
      {...(props.color ? { color: props.color } : {})}
      onSubmit={() => void submit.run(() => props.onConfirm({ reason: reason.trim(), password }))}
    >
      <p className={classes.muted}>{props.body}</p>
      {props.reason !== 'none' ? (
        <Textarea
          label={props.reasonLabel ?? t('hse.dialog.reason')}
          required={props.reason === 'required'}
          value={reason}
          autosize
          minRows={2}
          maxLength={2000}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
      ) : null}
      {props.password ? (
        <ReauthField
          reauth={reauth}
          label={t('hse.dialog.password')}
          description={t('hse.dialog.passwordHint')}
          mb="0"
        />
      ) : null}
    </Shell>
  );
}

/** Kaptan incelemesi (ortak §3): not, öncelik, sorumlu, hedef tarih. */
export function ReviewDialog(props: {
  opened: boolean;
  initial: { priority: string | null; responsible: string | null; targetDate: string | null };
  onClose: () => void;
  onConfirm: (input: {
    note: string | null;
    priority: string | null;
    responsible: string | null;
    targetDate: string | null;
  }) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [priority, setPriority] = useState(props.initial.priority);
  const [responsible, setResponsible] = useState(props.initial.responsible ?? '');
  const [targetDate, setTargetDate] = useState(props.initial.targetDate);
  const submit = useSubmit(props.onClose);
  return (
    <Shell
      opened={props.opened}
      title={t('hse.review.title')}
      onClose={props.onClose}
      error={submit.error}
      busy={submit.busy}
      confirmLabel={t('hse.review.confirm')}
      onSubmit={() =>
        void submit.run(() =>
          props.onConfirm({
            note: note.trim() || null,
            priority,
            responsible: responsible.trim() || null,
            targetDate,
          }),
        )
      }
    >
      <p className={classes.muted}>{t('hse.review.body')}</p>
      <div className={classes.dialogGrid}>
        <Select
          label={t('hse.fields.priority')}
          data={HSE_PRIORITIES.map((p) => ({ value: p, label: t(`hse.priorities.${p}`) }))}
          value={priority}
          onChange={setPriority}
        />
        <DayInput label={t('hse.fields.targetDate')} value={targetDate} onChange={setTargetDate} />
        <TextInput
          className={classes.full}
          label={t('hse.fields.responsible')}
          value={responsible}
          maxLength={200}
          onChange={(event) => setResponsible(event.currentTarget.value)}
        />
      </div>
      <Textarea
        label={t('hse.review.note')}
        value={note}
        autosize
        minRows={3}
        maxLength={4000}
        onChange={(event) => setNote(event.currentTarget.value)}
      />
    </Shell>
  );
}

/** Faaliyet atama ve düzeltme (ADR 0049 §5): tür, açıklama, sorumlu, hedef tarih, bulgu. */
export function ActionDialog(props: {
  opened: boolean;
  action: HseAction | null;
  findings: readonly HseFinding[];
  options: HseOptions | null;
  onClose: () => void;
  onConfirm: (input: HseActionInput) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const current = props.action;
  const [kind, setKind] = useState<string | null>(current?.kind ?? 'correction');
  const [description, setDescription] = useState(current?.description ?? '');
  const [responsibleUserId, setResponsibleUserId] = useState<string | null>(
    current?.responsibleUserId ?? null,
  );
  const [responsible, setResponsible] = useState(current?.responsible ?? '');
  const [dueDate, setDueDate] = useState<string | null>(current?.dueDate ?? null);
  const [findingId, setFindingId] = useState<string | null>(current?.findingId ?? null);
  const [workOrderId, setWorkOrderId] = useState<string | null>(current?.workOrderId ?? null);
  const submit = useSubmit(props.onClose);
  const people = props.options?.people ?? [];
  const missing = !kind || !description.trim() || !responsible.trim() || !dueDate;
  return (
    <Shell
      opened={props.opened}
      title={t(current ? 'hse.actions.editTitle' : 'hse.actions.newTitle')}
      onClose={props.onClose}
      error={submit.error}
      busy={submit.busy}
      confirmLabel={t(current ? 'hse.actions.save' : 'hse.actions.assign')}
      disabled={missing}
      onSubmit={() =>
        void submit.run(() =>
          props.onConfirm({
            kind: kind ?? 'correction',
            description: description.trim(),
            responsible: responsible.trim(),
            responsibleUserId,
            dueDate: dueDate ?? '',
            findingId,
            workOrderId,
            formRecordId: current?.formRecordId ?? null,
          }),
        )
      }
    >
      <div className={classes.dialogGrid}>
        <Select
          label={t('hse.actions.kind')}
          required
          data={HSE_ACTION_KINDS.map((k) => ({ value: k, label: t(`hse.actionKinds.${k}`) }))}
          value={kind}
          onChange={setKind}
          description={t(`hse.actionKindHints.${kind ?? 'correction'}`)}
        />
        <DayInput label={t('hse.actions.dueDate')} required value={dueDate} onChange={setDueDate} />
        <Textarea
          className={classes.full}
          label={t('hse.actions.description')}
          required
          value={description}
          autosize
          minRows={2}
          maxLength={2000}
          onChange={(event) => setDescription(event.currentTarget.value)}
        />
        <Select
          label={t('hse.actions.person')}
          data={people.map((p) => ({
            value: p.userId,
            label: p.roles.length > 0 ? `${p.name} (${p.roles.join(', ')})` : p.name,
          }))}
          value={responsibleUserId}
          clearable
          searchable
          onChange={(value) => {
            setResponsibleUserId(value);
            const person = people.find((p) => p.userId === value);
            if (person && !responsible.trim()) setResponsible(person.roles[0] ?? person.name);
          }}
        />
        <TextInput
          label={t('hse.actions.responsible')}
          required
          value={responsible}
          maxLength={200}
          onChange={(event) => setResponsible(event.currentTarget.value)}
        />
        {props.findings.length > 0 ? (
          <Select
            label={t('hse.actions.finding')}
            data={props.findings.map((f) => ({
              value: f.id,
              label: `${f.itemNo}. ${f.description}`,
            }))}
            value={findingId}
            clearable
            onChange={setFindingId}
          />
        ) : null}
        <Select
          label={t('hse.actions.workOrder')}
          data={(props.options?.workOrders ?? []).map((w) => ({
            value: w.id,
            label: `${w.no} · ${w.title}`,
          }))}
          value={workOrderId}
          clearable
          searchable
          onChange={setWorkOrderId}
        />
      </div>
    </Shell>
  );
}

/** Faaliyeti tamamla (ortak §7): not; kanıt ek ya da bağlı PMS iş emri / form kaydı. */
export function CompleteDialog(props: {
  opened: boolean;
  action: HseAction;
  options: HseOptions | null;
  onClose: () => void;
  onConfirm: (input: {
    note: string;
    workOrderId: string | null;
    formRecordId: string | null;
  }) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [workOrderId, setWorkOrderId] = useState<string | null>(props.action.workOrderId);
  const [formRecordId, setFormRecordId] = useState<string | null>(props.action.formRecordId);
  const submit = useSubmit(props.onClose);
  const evidence = props.action.files.filter((f) => !f.voided).length;
  return (
    <Shell
      opened={props.opened}
      title={t('hse.actions.completeTitle', { no: props.action.actionNo })}
      onClose={props.onClose}
      error={submit.error}
      busy={submit.busy}
      confirmLabel={t('hse.actions.complete')}
      disabled={!note.trim()}
      onSubmit={() =>
        void submit.run(() => props.onConfirm({ note: note.trim(), workOrderId, formRecordId }))
      }
    >
      <p className={classes.banner} data-tone={evidence > 0 ? 'success' : undefined}>
        {evidence > 0
          ? t('hse.actions.evidenceCount', { count: evidence })
          : t('hse.actions.evidenceNeeded')}
      </p>
      <Textarea
        label={t('hse.actions.completionNote')}
        required
        value={note}
        autosize
        minRows={3}
        maxLength={4000}
        onChange={(event) => setNote(event.currentTarget.value)}
      />
      <div className={classes.dialogGrid}>
        <Select
          label={t('hse.actions.workOrder')}
          data={(props.options?.workOrders ?? []).map((w) => ({
            value: w.id,
            label: `${w.no} · ${w.title}`,
          }))}
          value={workOrderId}
          clearable
          searchable
          onChange={setWorkOrderId}
        />
        <Select
          label={t('hse.actions.formRecord')}
          data={(props.options?.formRecords ?? []).map((f) => ({
            value: f.id,
            label: `${f.no} · ${f.title}`,
          }))}
          value={formRecordId}
          clearable
          searchable
          onChange={setFormRecordId}
        />
      </div>
    </Shell>
  );
}

/** PSC remark'ı ya da denetim bulgusu (ADR 0049 §9): kod serbest metin. */
export function FindingDialog(props: {
  opened: boolean;
  finding: HseFinding | null;
  onClose: () => void;
  onConfirm: (input: HseFindingInput) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const f = props.finding;
  const [description, setDescription] = useState(f?.description ?? '');
  const [category, setCategory] = useState(f?.category ?? '');
  const [regulationRef, setRegulationRef] = useState(f?.regulationRef ?? '');
  const [code, setCode] = useState(f?.code ?? '');
  const [actionTakenCode, setActionTakenCode] = useState(f?.actionTakenCode ?? '');
  const [dueDate, setDueDate] = useState<string | null>(f?.dueDate ?? null);
  const submit = useSubmit(props.onClose);
  return (
    <Shell
      opened={props.opened}
      title={t(f ? 'hse.findings.editTitle' : 'hse.findings.newTitle')}
      onClose={props.onClose}
      error={submit.error}
      busy={submit.busy}
      confirmLabel={t('hse.findings.save')}
      disabled={!description.trim()}
      onSubmit={() =>
        void submit.run(() =>
          props.onConfirm({
            description: description.trim(),
            category: category.trim() || null,
            regulationRef: regulationRef.trim() || null,
            code: code.trim() || null,
            actionTakenCode: actionTakenCode.trim() || null,
            dueDate,
            linkedReportId: f?.linkedReportId ?? null,
          }),
        )
      }
    >
      <Textarea
        label={t('hse.findings.issue')}
        required
        value={description}
        autosize
        minRows={2}
        maxLength={1000}
        onChange={(event) => setDescription(event.currentTarget.value)}
      />
      <div className={classes.dialogGrid}>
        <TextInput
          label={t('hse.findings.category')}
          value={category}
          maxLength={200}
          onChange={(event) => setCategory(event.currentTarget.value)}
        />
        <TextInput
          label={t('hse.findings.regulation')}
          value={regulationRef}
          maxLength={200}
          onChange={(event) => setRegulationRef(event.currentTarget.value)}
        />
        <TextInput
          label={t('hse.findings.code')}
          description={t('hse.findings.codeHint')}
          value={code}
          maxLength={20}
          onChange={(event) => setCode(event.currentTarget.value)}
        />
        <TextInput
          label={t('hse.findings.actionTaken')}
          value={actionTakenCode}
          maxLength={20}
          onChange={(event) => setActionTakenCode(event.currentTarget.value)}
        />
        <DayInput label={t('hse.findings.dueDate')} value={dueDate} onChange={setDueDate} />
      </div>
    </Shell>
  );
}

/** Ofis notu (ADR 0049 §7): yorum, ek faaliyet talebi, yeniden açma talebi. */
export function NoteDialog(props: {
  opened: boolean;
  onClose: () => void;
  onConfirm: (input: {
    kind: 'comment' | 'action_request' | 'reopen_request';
    body: string;
  }) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<'comment' | 'action_request' | 'reopen_request'>('comment');
  const [body, setBody] = useState('');
  const submit = useSubmit(() => {
    setBody('');
    props.onClose();
  });
  return (
    <Shell
      opened={props.opened}
      title={t('hse.notes.title')}
      onClose={props.onClose}
      error={submit.error}
      busy={submit.busy}
      confirmLabel={t('hse.notes.send')}
      disabled={!body.trim()}
      onSubmit={() => void submit.run(() => props.onConfirm({ kind, body: body.trim() }))}
    >
      <Select
        label={t('hse.notes.kind')}
        data={(['comment', 'action_request', 'reopen_request'] as const).map((k) => ({
          value: k,
          label: t(`hse.noteKinds.${k}`),
        }))}
        value={kind}
        allowDeselect={false}
        onChange={(value) => setKind((value ?? 'comment') as typeof kind)}
      />
      <Textarea
        label={t('hse.notes.body')}
        required
        value={body}
        autosize
        minRows={3}
        maxLength={4000}
        onChange={(event) => setBody(event.currentTarget.value)}
      />
    </Shell>
  );
}

/** Ofisin rapor açma talebi (ADR 0049 §7): gemi taslağı talepten açar. */
export function RequestDialog(props: {
  opened: boolean;
  type: HseReportType;
  vessels: readonly { id: string; name: string }[];
  onClose: () => void;
  onConfirm: (input: {
    vesselId: string;
    requestedType: HseReportType;
    title: string;
    body: string;
  }) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [vesselId, setVesselId] = useState<string | null>(props.vessels[0]?.id ?? null);
  const [type, setType] = useState<HseReportType>(props.type);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const submit = useSubmit(props.onClose);
  return (
    <Shell
      opened={props.opened}
      title={t('hse.requests.title')}
      onClose={props.onClose}
      error={submit.error}
      busy={submit.busy}
      confirmLabel={t('hse.requests.send')}
      disabled={!vesselId || !title.trim() || !body.trim()}
      onSubmit={() =>
        void submit.run(() =>
          props.onConfirm({
            vesselId: vesselId ?? '',
            requestedType: type,
            title: title.trim(),
            body: body.trim(),
          }),
        )
      }
    >
      <p className={classes.muted}>{t('hse.requests.body')}</p>
      <div className={classes.dialogGrid}>
        <Select
          label={t('hse.list.vessel')}
          required
          data={props.vessels.map((v) => ({ value: v.id, label: v.name }))}
          value={vesselId}
          onChange={setVesselId}
        />
        <Select
          label={t('hse.requests.type')}
          required
          data={HSE_REPORT_TYPES.map((v) => ({ value: v, label: t(`hse.types.${v}`) }))}
          value={type}
          allowDeselect={false}
          onChange={(value) => setType((value ?? props.type) as HseReportType)}
        />
      </div>
      <TextInput
        label={t('hse.fields.title')}
        required
        value={title}
        maxLength={200}
        onChange={(event) => setTitle(event.currentTarget.value)}
      />
      <Textarea
        label={t('hse.fields.description')}
        required
        value={body}
        autosize
        minRows={3}
        maxLength={4000}
        onChange={(event) => setBody(event.currentTarget.value)}
      />
    </Shell>
  );
}

/**
 * Rapordan iş emri (H3, H4; ADR 0097 §7): raporun iş emri varsa yenisi açılmaz, o iş emri açılır;
 * yoksa ekipman (raporunki ya da seçilen), başlık ve hedef tarihle düzeltici iş emri.
 */
export function WorkOrderDialog(props: {
  opened: boolean;
  detail: HseDetail;
  options: HseOptions | null;
  onClose: () => void;
  onOpenWorkOrder: (id: string) => void;
  onConfirm: (input: HseWorkOrderInput) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const { detail } = props;
  const existing = detail.links.workOrder;
  const [equipmentId, setEquipmentId] = useState<string | null>(detail.report.equipmentId);
  const [title, setTitle] = useState(`${detail.reportNo} ${detail.title}`.slice(0, 200));
  const [targetOn, setTargetOn] = useState<string | null>(detail.targetDate);
  const submit = useSubmit(props.onClose);
  if (existing) {
    return (
      <Shell
        opened={props.opened}
        title={t('hse.hseq.workOrder.title')}
        onClose={props.onClose}
        error={null}
        busy={false}
        confirmLabel={t('hse.hseq.workOrder.open')}
        onSubmit={() => {
          props.onOpenWorkOrder(existing.id);
          props.onClose();
        }}
      >
        <p className={classes.banner}>{t('hse.hseq.workOrder.exists', { no: existing.no })}</p>
      </Shell>
    );
  }
  return (
    <Shell
      opened={props.opened}
      title={t('hse.hseq.workOrder.title')}
      onClose={props.onClose}
      error={submit.error}
      busy={submit.busy}
      confirmLabel={t('hse.hseq.workOrder.create')}
      disabled={!title.trim()}
      onSubmit={() =>
        void submit.run(() =>
          props.onConfirm(workOrderInput(detail.targetDate, { equipmentId, title, targetOn })),
        )
      }
    >
      {detail.links.defect ? (
        <p className={classes.muted}>{t('hse.hseq.workOrder.fromDefect')}</p>
      ) : null}
      <Select
        label={t('hse.hseq.workOrder.equipment')}
        required
        searchable
        data={(props.options?.equipment ?? []).map((e) => ({
          value: e.id,
          label: `${e.code} · ${e.name}`,
        }))}
        value={equipmentId}
        onChange={setEquipmentId}
      />
      <TextInput
        label={t('hse.hseq.workOrder.name')}
        required
        maxLength={200}
        value={title}
        onChange={(event) => setTitle(event.currentTarget.value)}
      />
      <DayInput label={t('hse.hseq.workOrder.target')} value={targetOn} onChange={setTargetOn} />
    </Shell>
  );
}

export interface SmsUpdateValue {
  needed: boolean | null;
  procedure: string | null;
  proposal: string | null;
}

/**
 * SMS prosedür güncellemesi (H10; ADR 0097 §7): gemide doküman değişiklik talebi akışı yok;
 * ihtiyaç rapora yazılır (LGS-17 SMS bölümü kalıbı), PDF'te görünür, ofis revizyonla izler.
 */
export function SmsUpdateDialog(props: {
  opened: boolean;
  initial: SmsUpdateValue | null;
  readOnly: boolean;
  onClose: () => void;
  onConfirm: (value: SmsUpdateValue) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [needed, setNeeded] = useState(props.initial?.needed ?? true);
  const [procedure, setProcedure] = useState(props.initial?.procedure ?? '');
  const [proposal, setProposal] = useState(props.initial?.proposal ?? '');
  const submit = useSubmit(props.onClose);
  return (
    <Shell
      opened={props.opened}
      title={t('hse.hseq.sms.title')}
      onClose={props.onClose}
      error={submit.error}
      busy={submit.busy}
      confirmLabel={t('hse.hseq.sms.save')}
      disabled={props.readOnly}
      onSubmit={() =>
        void submit.run(() =>
          props.onConfirm({
            needed,
            procedure: procedure.trim() || null,
            proposal: proposal.trim() || null,
          }),
        )
      }
    >
      <p className={classes.muted}>{t('hse.hseq.sms.hint')}</p>
      {props.readOnly ? <p className={classes.muted}>{t('hse.closure.saveFirst')}</p> : null}
      <Switch
        label={t('hse.hseq.sms.needed')}
        checked={needed === true}
        disabled={props.readOnly}
        onChange={(event) => setNeeded(event.currentTarget.checked)}
      />
      <TextInput
        label={t('hse.hseq.sms.procedure')}
        maxLength={300}
        readOnly={props.readOnly}
        value={procedure}
        onChange={(event) => setProcedure(event.currentTarget.value)}
      />
      <Textarea
        label={t('hse.hseq.sms.proposal')}
        autosize
        minRows={3}
        maxLength={2000}
        readOnly={props.readOnly}
        value={proposal}
        onChange={(event) => setProposal(event.currentTarget.value)}
      />
    </Shell>
  );
}

/** HSEQ listesinden yeni rapor: tür seçimi (NC / DF / NM / ACC). */
export function TypeChoiceDialog(props: {
  opened: boolean;
  onClose: () => void;
  onChoose: (type: HseReportType) => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      opened={props.opened}
      onClose={props.onClose}
      title={t('hse.hseq.chooseType')}
      closeButtonProps={{ 'aria-label': t('hse.dialog.close') }}
    >
      <div className={classes.cardBody}>
        {HSE_REPORT_TYPES.map((type) => (
          <Button
            key={type}
            variant="default"
            fullWidth
            justify="flex-start"
            onClick={() => props.onChoose(type)}
          >
            {t(`hse.newTitle.${type}`)}
          </Button>
        ))}
      </div>
    </Modal>
  );
}
